import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CartContext = createContext(null);

const CART_STORAGE_KEY = 'pp_store_cart';
const TX_SALES_TAX = 0.0625;
const FLAT_RATE_SHIPPING = 9.99;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem(CART_STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [isCartOpen, setIsCartOpen] = useState(false);

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product, quantity = 1) => {
    setItems(prev => {
      const existing = prev.find(item => item.product_id === product.id);
      if (existing) {
        return prev.map(item =>
          item.product_id === product.id
            ? { ...item, quantity: Math.min(item.quantity + quantity, product.stock_quantity || 99) }
            : item
        );
      }
      return [...prev, {
        product_id: product.id,
        name: product.name,
        slug: product.slug,
        price: product.sale_price || product.price,
        image: product.images?.[0] || null,
        quantity: Math.min(quantity, product.stock_quantity || 99),
        max_quantity: product.stock_quantity || 99
      }];
    });
    setIsCartOpen(true);
    setTimeout(() => setIsCartOpen(false), 2000);
  }, []);

  const removeItem = useCallback((productId) => {
    setItems(prev => prev.filter(item => item.product_id !== productId));
  }, []);

  const updateQuantity = useCallback((productId, quantity) => {
    if (quantity <= 0) {
      setItems(prev => prev.filter(item => item.product_id !== productId));
      return;
    }
    setItems(prev =>
      prev.map(item =>
        item.product_id === productId
          ? { ...item, quantity: Math.min(quantity, item.max_quantity) }
          : item
      )
    );
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const taxAmount = Math.round(subtotal * TX_SALES_TAX * 100) / 100;
  const shippingCost = items.length > 0 ? FLAT_RATE_SHIPPING : 0;
  const total = Math.round((subtotal + taxAmount + shippingCost) * 100) / 100;

  const value = {
    items,
    itemCount,
    subtotal,
    taxAmount,
    shippingCost,
    total,
    isCartOpen,
    setIsCartOpen,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    FLAT_RATE_SHIPPING,
    TX_SALES_TAX
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext;
